import BigNumber from 'bignumber.js';
import { ScrollView, View } from 'dripsy';
import { t } from 'i18next';
import React, { useContext, useState } from 'react';

import { AccountSelector } from '@/design-system/components/accountSelector/AccountSelector';
import { UniButton } from '@/design-system/components/buttons';
import { UniInputAmount } from '@/design-system/components/inputs';
import { UniInputVariant } from '@/design-system/components/inputs/useInputTheme.hook';
import { ModalHeader } from '@/design-system/components/modal';
import { UniTextDetailsCard } from '@/design-system/components/cards';
import { UniNotes } from '@/design-system/components/notes/UniNote';
import { LayoutContext } from 'contexts/LayoutContext';
import { AutoRenewalSwitch, CurrenciesIcon } from '../../components';
import { AmountSummary } from '../../components/cards/AmountSummary';
import { getEarnTenorTranslationKey } from '../../utils/getEarnTenorTranslationKey';
import { SubscribeStackRoutes, SubscribeStackScreenProps } from './type';
import { useSubscribeEdit } from './useSubscribeEdit.hook';
import { UniTabHeader } from '@/design-system/components/tabs';
import { AlertType, UniAlert } from '@/design-system/components/alerts';
import { TextCaption } from '@/design-system/components/typography';
import { formatAmountByDecimals } from '@/utils/numberFormatting';
import { CurrenciesContext } from '@/currencies/currency.context';
import Markdown from 'react-native-markdown-display';

export const SubscribeEdit: React.FC<SubscribeStackScreenProps<SubscribeStackRoutes.Main>> = ({ navigation, route }) => {
  const { offerId, onLayout } = route.params;
  const layout = useContext(LayoutContext);
  const { getDecimalByAsset } = useContext(CurrenciesContext);
  const { offerDetails, form, preview, errorText, onSubmit } = useSubscribeEdit({ offerId, onLayout });
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  const data = offerDetails?.data?.data;
  const isFlexible = !data?.tenor;

  const tenor = getEarnTenorTranslationKey(data?.tenor);

  const accountBalance = new BigNumber(form.account?.balance || 0).toNumber();
  const maxSubscriptionAmount = new BigNumber(data?.maxSubscriptionAmount || Infinity).toNumber();

  const Notes = {
    Flexible: [t('earn-invest::glossary.subscription-note-apy')],
    Locked: [t('earn-invest::glossary.subscription-note')]
  };

  return (
    <View
      sx={{
        height: layout.isMobile ? '100%' : 'auto',
        maxHeight: layout.isMobile ? '100%' : Math.min(900, layout.dimensions.height),
        width: layout.dimensions.width,
        maxWidth: layout.isMobile ? layout.dimensions.width : Math.min(500, layout.dimensions.width),
        backgroundColor: '$backgroundPrimary'
      }}
      onLayout={(event) => {
        const { width, height } = event.nativeEvent.layout;
        setDimensions({ width, height });
        onLayout(event);
      }}>
      {/* Content */}

      <Header title={data?.name || ''} isMobile={layout.isMobile} onClose={() => navigation.popToTop()} />
      <ScrollView>
        <View
          sx={{
            height: layout.isMobile ? dimensions.height - 43 : 'auto',
            padding: layout.isMobile ? '$5' : '$7',
            gap: '$7',
            justifyContent: layout.isMobile ? 'space-between' : 'flex-start'
          }}>

          <View sx={{ gap: '$7' }}>
            <CurrenciesIcon asset={data?.asset || ''} loading={offerDetails.isFetching} />
            <Markdown>{offerDetails.data?.data?.description}</Markdown>
            <View sx={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <AmountSummary
                label={t('earn-invest::glossary.est-apy')}
                value={data ? data?.estimatedApy.toString() : ''}
                asset="%"
                success
                loading={offerDetails.isFetching}
              />
              <AmountSummary
                label={t('earn-invest::glossary.tenor')}
                value={tenor.key ? t(tenor.key, tenor.options) : ''}
                asset=""
                loading={offerDetails.isFetching}
                alignRight
              />
            </View>
            <View sx={{ gap: '$5' }}>
              <AccountSelector
                asset={data?.asset}
                onValueChange={(value) => form.handleValueChange('walletId', value)}
                value={form.values.walletId}
                sx={{ mb: 0 }}
              />
              <UniInputAmount
                variant={UniInputVariant.v1}
                labelText={t('earn-invest::glossary.subscription-amount')}
                labelRightText={
                  data?.lotSize
                    ? `${formatAmountByDecimals({ value: data.lotSize, decimals: getDecimalByAsset(data.asset), suffix: data.asset })} / ${t(
                      'earn-invest::glossary.unit'
                    )}`
                    : ''
                }
                value={form.values.subscriptionAmount}
                balance={form.account?.balance}
                asset={data?.asset}
                onValueChange={(value) => form.handleValueChange('subscriptionAmount', value)}
                max={Math.floor(Math.min(maxSubscriptionAmount, accountBalance) / (data?.lotSize || 1)) * (data?.lotSize || 1)}
                error={!!form.errorText}
                errorText={(form.errorText && t(form.errorText)) || undefined}
                errorTextPadding={20}
                formatValueOnBlur={form.formatSubscriptionAmountOnBlur}
                validator={form.validator}
              />
              <UniTextDetailsCard data={offerDetails.textDetailsData} />
              {data?.autoRenewable ? (
                <AutoRenewalSwitch
                  value={!!form.values.autoRenew}
                  onValueChange={(value) => form.handleValueChange('autoRenew', value)}
                  sx={{ mb: 0 }}
                />
              ) : null}
              {!isFlexible ? (
                <UniNotes title={t('earn-invest::glossary.note')} paragraphs={isFlexible ? Notes.Flexible : Notes.Locked} isList />
              ) : null}
            </View>
            {errorText ? <UniAlert type={AlertType.Error} message={errorText} /> : null}
          </View>
          <View sx={{}}>
            <UniButton
              size="default"
              onPress={onSubmit}
              label={t('earn-invest::glossary.continue')}
              wrapperSx={{ width: '100%' }}
              disabled={form.disableSubmit}
              loading={preview.isLoading}
            />
          </View>
        </View>
      </ScrollView>
    </View>
  );
};

const Header = ({ title, isMobile, onClose }: { title: string; isMobile: boolean; onClose: () => void }) => {
  if (isMobile) {
    return <UniTabHeader title={title} onBack={onClose} />;
  }

  return <ModalHeader title={title} closeModal={onClose} />;
};
